import React, { useRef, useState } from 'react';
import { SlashCommandMenu } from '../../../components/SlashCommandMenu';
import { applySlashCommand } from '../../../utils/slashCommands';

/**
 * "/" quick-insert menu. Selecting doc / variable / math swaps this overlay
 * for the matching picker at the same caret; other commands convert the block.
 */
export const useSlashMenu = ({
  block,
  contentEditableRef,
  onUpdate,
  onUpdateAndInsert,
  hasVariablesBlock,
  openDocPicker,
  openMathFromSlash,
  openVarFromSlash,
  excludeIds = null,
}) => {
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const slashMenuOpenRef = useRef(false);
  const slashTextNodeRef = useRef(null);

  const closeSlashMenu = () => {
    setShowSlashMenu(false);
    slashMenuOpenRef.current = false;
  };

  const handleSlashSelect = (commandId) => {
    // Keep whatever was typed before the "/" — drop the "/query" part
    const textContent = contentEditableRef.current?.innerText || '';
    const lastSlashIndex = textContent.lastIndexOf('/');
    const beforeSlash =
      lastSlashIndex !== -1 ? textContent.substring(0, lastSlashIndex) : textContent;
    const queryLength =
      lastSlashIndex !== -1 ? textContent.length - lastSlashIndex - 1 : 0;
    const slashCtx = { textNode: slashTextNodeRef.current, queryLength };

    if (commandId === 'doc') {
      closeSlashMenu();
      openDocPicker({ position, ...slashCtx });
      return;
    }

    if (commandId === 'variable') {
      closeSlashMenu();
      openVarFromSlash(position, slashCtx);
      return;
    }

    if (commandId === 'math') {
      closeSlashMenu();
      openMathFromSlash(position, slashCtx);
      return;
    }

    if (excludeIds?.includes(commandId)) {
      closeSlashMenu();
      return;
    }

    applySlashCommand({
      commandId,
      block,
      beforeSlash,
      onUpdate,
      onUpdateAndInsert,
      hasVariablesBlock,
    });
    closeSlashMenu();
  };

  const tryOpenFromSlashKey = (e) => {
    if (e.key !== '/' || slashMenuOpenRef.current) return false;

    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const textBeforeCursor =
        range.startContainer.textContent?.substring(0, range.startOffset) || '';
      const lastChar = textBeforeCursor[textBeforeCursor.length - 1];
      if (!lastChar || lastChar === ' ' || lastChar === '\n') {
        setTimeout(() => {
          const sel = window.getSelection();
          if (sel.rangeCount > 0) {
            const nextRange = sel.getRangeAt(0);
            const rect = nextRange.getBoundingClientRect();
            // The text node holding the "/" — kept for /doc /variable /math
            // inserts, which run after focus has moved into the picker
            slashTextNodeRef.current = nextRange.startContainer;
            setPosition({
              x: rect.left,
              y: rect.bottom + 4,
              caretTop: rect.top,
            });
            setShowSlashMenu(true);
            slashMenuOpenRef.current = true;
            setSearchQuery('');
          }
        }, 0);
      }
    }
    return true;
  };

  const updateSlashQuery = () => {
    if (!slashMenuOpenRef.current) return;
    const textContent = contentEditableRef.current?.innerText || '';
    const lastSlashIndex = textContent.lastIndexOf('/');
    if (lastSlashIndex !== -1) {
      setSearchQuery(textContent.substring(lastSlashIndex + 1));
    } else {
      closeSlashMenu();
    }
  };

  const tryCloseSlashOnBackspace = (e) => {
    if (e.key !== 'Backspace' || !slashMenuOpenRef.current) return;
    const textContent = contentEditableRef.current?.innerText || '';
    const lastSlashIndex = textContent.lastIndexOf('/');
    if (lastSlashIndex !== -1 && textContent.substring(lastSlashIndex + 1).length === 0) {
      closeSlashMenu();
    }
  };

  const slashMenu = showSlashMenu ? (
    <SlashCommandMenu
      position={position}
      onSelect={handleSlashSelect}
      onClose={closeSlashMenu}
      searchQuery={searchQuery}
      hasVariablesBlock={hasVariablesBlock}
      excludeIds={excludeIds}
    />
  ) : null;

  return {
    slashMenuOpenRef,
    slashMenu,
    tryOpenFromSlashKey,
    updateSlashQuery,
    tryCloseSlashOnBackspace,
  };
};
